import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { DeviceEvent } from "../../../api/types/Camera";
import { VehicleStatusCode } from "../../../api/types/VehicleStatusCode";
import svgIcons from "../../../assets/svg/svgIcons";
import { mapVehicleEventIconWrapped } from "../../../assets/svg/vehicle-event-wrapped";
import { mapVehicleIconWrapped } from "../../../assets/svg/vehicle-wrapped";
import { APP_CONFIG } from "../../../constants/constants";
import { TMapOperations, TMPushpin } from "./type";
import { CustomRenderer } from "../../../utils/map";

export const mapOperations: TMapOperations = (props) => {
  console.log("[mapOperations]", props);
  const google = (window as any).google;

  // create a namespace for map objects
  props.mapRef.current.objects = {
    mPushpins: [],
    mClusterLayer: null,
  };

  const motionIconSize = APP_CONFIG.MAPS.VEHICLE_EVENT_ICON_SIZE;
  const eventIconSize = APP_CONFIG.MAPS.VEHICLE_EVENT_ICON_SIZE * 1.6;

  const renderMapObjects = async () => {
    const MAX_WAYPOINTS = 25;
    const MAX_VIAPOINTS = 10;
    const G_MAX_WAYPOINTS = 25;
    const { AdvancedMarkerElement } =
      await google?.maps?.importLibrary("marker");
    const map = props.mapRef.current.map;
    // determine snapped points and save them to LS - to prevent repeated calls
    // finally use snapped points to create waypoints

    const tripEventMarkerArr: any[] = [];

    props.dataPoints
      // .filter((tripPoint) => deviceEventPoint.length > 1)
      .forEach((deviceEventPoint, pointIndex) => {
        const displayEventPointOnMap = (sanitizedTripPoint: DeviceEvent) => {
          const isMotionEvent =
            deviceEventPoint.statuscode ===
              VehicleStatusCode.STATUS_MOTION_HEADING ||
            deviceEventPoint.statuscode ===
              VehicleStatusCode.STATUS_MOTION_MOVING ||
            deviceEventPoint.statuscode ===
              VehicleStatusCode.STATUS_MOTION_ENROUTE ||
            deviceEventPoint.statuscode === VehicleStatusCode.STATUS_DISTANCE ||
            deviceEventPoint.statuscode ===
              VehicleStatusCode.STATUS_DISTANCE_1 ||
            deviceEventPoint.statuscode === VehicleStatusCode.STATUS_DISTANCE_2;
          // || deviceEventPoint.statuscode === VehicleStatusCode.STATUS_MOTION_EN_ROUTE // typescript giving unintentional comparison error here probably because STATUS_MOTION_ENROUTE is equal to STATUS_MOTION_EN_ROUTE

          let icon = null;

          icon = isMotionEvent
            ? // driving icon with orientation
              mapVehicleIconWrapped(
                "driving",
                false,
                deviceEventPoint.heading || 0
              )
            : // event icon
              mapVehicleEventIconWrapped(
                deviceEventPoint.statuscode,
                false,
                eventIconSize
              );

          // create a pushpin for the trip point event
          // Set anchor point based on motion or event status
          const anchorPoint = isMotionEvent
            ? { x: motionIconSize * 0.5, y: motionIconSize * 0.5 }
            : { x: eventIconSize * 0.5, y: eventIconSize * 0.8 };
          const parseSvgToDom = (svgString: string) => {
            const parser = new DOMParser();
            return parser.parseFromString(svgString, "image/svg+xml")
              .documentElement;
          };
          const pinSvg = parseSvgToDom(icon);
          // Create the marker
          const tripEventMarker = new AdvancedMarkerElement({
            position: {
              lat: deviceEventPoint.latitude,
              lng: deviceEventPoint.longitude,
            },
            map,
            content: pinSvg,
            title: `${deviceEventPoint.deviceid}`, // used to identify which markers fall under a cluster and show infobox accordingly
          });

          // this auxMarker is meant for debugging and should be enabled only if debugging is required
          // const auxMarker = new google.maps.Marker({
          //   position: {
          //     lat: deviceEventPoint.latitude,
          //     lng: deviceEventPoint.longitude,
          //   },
          //   map: map,
          // });
          // auxMarker.setMap(map);

          // Apply anchor (offset) using CSS transform
          // 3/26/2025 Disabled the transform styles below as this is causing the dot to be off the actual location
          // tripEventMarker.content.style.transform = `translate(-${anchorPoint.x}px, -${anchorPoint.y}px)`;
          // 4/4/2024 following style helps to position the marker correctly
          if (isMotionEvent) {
            // to position the enroute arrow icon correctly
            tripEventMarker.content.style.transform = "translateY(50%)";
          } else {
            // to position other event icons (white marker shape) correctly
            tripEventMarker.content.style.transform = "translateY(15%)";
          }

          // open an infowindow with details
          const headerContent = document.createElement("h2");
          headerContent.className =
            "ps-4 pb-1 text-sm font-semibold text-accent-blue-dark";
          // headerContent.innerHTML = deviceEventPoint.status.description;
          headerContent.innerHTML = Array.from(
            new Set([
              deviceEventPoint.cameraid,
              deviceEventPoint.deviceid,
              deviceEventPoint.device_description,
            ])
          )
            .filter((item) => !!item)
            .join(" | ");
          const infobox = new google.maps.InfoWindow({
            content: mapDeviceEventInfoBoxContent(
              deviceEventPoint,
              props?.compact ? undefined : `event-${deviceEventPoint.recordid}`,
              true, // hide device id section
              props?.showAltitude
            ),
            // headerDisabled: true,
            headerContent,
            maxWidth: 300,
            // pixelOffset: new google.maps.Size(16, -16),
            zIndex: APP_CONFIG.MAPS.INFOBOX_ZINDEX,
          });

          // Add click event listener
          tripEventMarker.addEventListener("click", () => {
            props.onDataPointPushpinClick?.(deviceEventPoint);
            // show infobox on click
            infobox.open({
              anchor: tripEventMarker,
              map: map,
            });
          });

          // close infowindow on clicking anywhere on the map
          // google.maps.event.addListener(map, "click", function () {
          //   infobox.close();
          // });

          // add the pushpin to the objects
          const pushpinObject: TMPushpin = {
            index: pointIndex,
            recordId: deviceEventPoint.recordid,
            marker: tripEventMarker,
            pointData: deviceEventPoint,
          };
          props.mapRef.current.objects.mPushpins.push(pushpinObject);

          tripEventMarkerArr.push(tripEventMarker);
        };

        displayEventPointOnMap(deviceEventPoint);
      });

    // if clustering is enabled...
    // Create a ClusterLayer and add it to the map.
    if (props.mapLayerOptions?.clustering) {
      new MarkerClusterer({
        markers: tripEventMarkerArr,
        map: map,
        renderer: new CustomRenderer(
          props.dataPoints as DeviceEvent[],
          "cameras"
        ),
      });
    }

    // center the map to the polygon
    setTimeout(() => mapCenterToDataPoints(props, props.dataPoints), 1000);

    return null;
  };
  renderMapObjects();
};

const mapCenterToDataPoints: TMapOperations = async (props, value) => {
  const google = window.google;
  const googleMapsCore: any = await google?.maps?.importLibrary?.("core");
  if (!googleMapsCore || !googleMapsCore.LatLngBounds) return;
  const { LatLngBounds } = googleMapsCore;
  if (!props.dataPoints || props.dataPoints.length < 1) return;
  const bounds = new LatLngBounds();
  props.dataPoints
    // .reduce((acc, trip, index) => {
    //   if (
    //     props?.selectedTripIndex === null ||
    //     index === props?.selectedTripIndex
    //   ) {
    //     acc.push(...trip);
    //   }
    //   return acc;
    // }, [])
    .forEach((dataPoint) => {
      bounds.extend(
        new google.maps.LatLng(dataPoint.latitude, dataPoint.longitude)
      );
    });

  if (!bounds.isEmpty()) {
    props.mapRef.current.map?.fitBounds(bounds, 20);
  }
};

export const mapDeviceEventInfoBoxContent = (
  dataPoint: DeviceEvent,
  detailsButtonRowId?: string,
  hideDeviceIdSection?: boolean,
  showAltitude?: boolean
) => {
  return (
    `<div class="vehicle-infobox-content bg-white Xoutline -outline-offset-2 outline-accent-blue-dark/20 rounded-lg text-sm leading-none break-all flex flex-col gap-2 min-w-56 px-4 pb-4 relative">` +
    // <!-- h4 class="pt-2 font-semibold text-accent-blue-dark">${dataPoint.status.description}</h4 -->
    (!!dataPoint.deviceid && !hideDeviceIdSection
      ? `<p class="pt-1 flex items-center gap-1"><img src="${svgIcons.CameraEventSpecCamera}" />${dataPoint.deviceid}</p>`
      : "") +
    (!!dataPoint.drivername
      ? `<p class="pt-1 flex items-center gap-1"><img src="${svgIcons.CameraEventSpecDriver}" />${dataPoint.drivername}</p>`
      : "") +
    (!!dataPoint.latitude && dataPoint.longitude
      ? `<p class="py-1 flex items-center gap-1"><img src="${svgIcons.CameraEventSpecLocation}" />${dataPoint?.address ?? ""}</p>`
      : "") +
    (showAltitude && dataPoint.altitude !== undefined && dataPoint.altitude !== null
      ? `<p class="py-1 flex items-center gap-1">Altitude: ${dataPoint.altitude}m</p>`
      : "") +
    (!!detailsButtonRowId
      ? `<button type="button" class="h-10 rounded-md border border-accent-blue-dark font-semibold hover:bg-accent-blue-dark text-accent-blue-dark hover:text-white" onclick="document.querySelector('#${detailsButtonRowId}')?.click()">Details</button>`
      : "") +
    `</div>`
  );
};
